// Shared helper: refresh cart drawer using the ?section_id= endpoint.
// Works in both shopify theme dev (127.0.0.1) and production.
async function _refreshCartDrawer(drawer) {
  const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart';
  const themeParam = window.Shopify?.theme?.id ? `&preview_theme_id=${window.Shopify.theme.id}` : '';
  const [drawerHtml, iconHtml] = await Promise.all([
    fetch(`${cartUrl}?section_id=cart-drawer${themeParam}`).then(r => r.text()).catch(() => null),
    fetch(`${cartUrl}?section_id=cart-icon-bubble${themeParam}`).then(r => r.text()).catch(() => null),
  ]);
  if (drawerHtml) {
    const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
    const newCartDrawer = doc.querySelector('#CartDrawer');
    const liveCartDrawer = document.querySelector('#CartDrawer');
    if (newCartDrawer && liveCartDrawer) {
      liveCartDrawer.innerHTML = newCartDrawer.innerHTML;
    }
    // .drawer__inner-empty is present in section HTML only when cart == empty
    drawer.classList.toggle('is-empty', !!doc.querySelector('.drawer__inner-empty'));
  }
  if (iconHtml) {
    const iconEl = document.getElementById('cart-icon-bubble');
    const src = new DOMParser().parseFromString(iconHtml, 'text/html').querySelector('.shopify-section');
    if (iconEl && src) iconEl.innerHTML = src.innerHTML;
  }
  // Re-attach overlay listener since innerHTML was replaced
  const overlay = document.getElementById('CartDrawer-Overlay');
  if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
  drawer.open();
}

class BundleSet {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    const dataEl = document.getElementById(`BundleData-${this.sectionId}`);
    if (!dataEl) { console.error(`[BundleSet] BundleData-${this.sectionId} not found`); return; }

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[BundleSet] Failed to parse data island JSON:', e, '\nRaw:', dataEl.textContent);
      return;
    }

    console.log('[BundleSet] init', this.sectionId, this.data);

    const def = this.data.defaults;

    // Colour can be deep-linked from the homepage set grid:
    //   ?color=White          -> both shirt and trousers
    //   ?shirt=White&trouser=Black -> each independently
    // Anything not matching an actual swatch is ignored and the defaults stand.
    const initial = this._coloursFromUrl(def);

    // Mix-only: shirt colour + trouser colour selected independently (Match removed).
    this.mode = 'mix';
    this.sel = { shirtColor: initial.shirt, trouserColor: initial.trouser, shirtSize: null, trouserSize: null };
    // Track what the *server* rendered, not what we're about to select — otherwise
    // _updateImage()'s no-op guard fires and a deep-linked colour keeps the
    // default image. If the URL matches the default this stays a genuine no-op.
    this.currentImageKey = `${def.mixShirtColor}-${def.mixTrouserColor}`;
    this.image = document.getElementById(`BundleImage-${this.sectionId}`);
    this.cta    = document.getElementById(`BundleCta-${this.sectionId}`);
    this.buyNow = document.getElementById(`BundleBuyNow-${this.sectionId}`);
    this.error  = document.getElementById(`BundleError-${this.sectionId}`);

    if (!this.cta) { console.error(`[BundleSet] BundleCta-${this.sectionId} not found`); return; }

    this._bindSwatches();
    this._bindSizes();
    this._bindThumbnails();
    this._bindThumbArrows();
    this.cta.addEventListener('click', () => this._addToCart({ checkout: false }));
    this.buyNow?.addEventListener('click', () => this._addToCart({ checkout: true }));

    // Pre-select colours (URL-provided or default) + sizes
    this._setSwatchSelected('shirt', initial.shirt);
    this._setSwatchSelected('trouser', initial.trouser);
    this._updateSizes('shirt', initial.shirt);
    this._updateSizes('trouser', initial.trouser);
    this._updateImage();
    this._updateThumbActive();
  }

  /**
   * Resolve the starting colours from the query string, falling back to the
   * section defaults. Matching is case-insensitive but the value returned is
   * always the swatch's own casing, since the image/variant maps are keyed on it.
   */
  _coloursFromUrl(def) {
    const fallback = { shirt: def.mixShirtColor, trouser: def.mixTrouserColor };

    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return fallback;
    }

    const optionsFor = (ctx) =>
      Array.from(this.container.querySelectorAll(`.bundle-set__swatch[data-context="${ctx}"]`))
        .map((el) => el.dataset.color)
        .filter(Boolean);

    const resolve = (raw, ctx) => {
      if (!raw) return null;
      const wanted = raw.trim().toLowerCase();
      return optionsFor(ctx).find((c) => c.toLowerCase() === wanted) || null;
    };

    const both = params.get('color');
    return {
      shirt: resolve(params.get('shirt') || both, 'shirt') || fallback.shirt,
      trouser: resolve(params.get('trouser') || both, 'trouser') || fallback.trouser,
    };
  }

  _bindSwatches() {
    this.container.querySelectorAll('.bundle-set__swatch').forEach((sw) => {
      sw.addEventListener('click', () => {
        const { color, context } = sw.dataset;
        this._setSwatchSelected(context, color);
        if (context === 'shirt') {
          this.sel.shirtColor = color;
          this._updateSizes('shirt', color);
        } else {
          this.sel.trouserColor = color;
          this._updateSizes('trouser', color);
        }
        this._updateImage();
        this._updateThumbActive();
      });
    });
  }

  /* Thumbnails: each represents a shirt+trouser combo. Click → set both colours,
     swap image, and reset sizes to default / next available. */
  _bindThumbnails() {
    this.container.querySelectorAll('.bundle-set__thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const sc = thumb.dataset.shirtColor;
        const tc = thumb.dataset.trouserColor;
        if (!sc || !tc) return;
        this.sel.shirtColor = sc;
        this.sel.trouserColor = tc;
        this._setSwatchSelected('shirt', sc);
        this._setSwatchSelected('trouser', tc);
        this._updateSizes('shirt', sc);
        this._updateSizes('trouser', tc);
        this._updateImage();
        this._updateThumbActive();
      });
    });
  }

  _updateThumbActive() {
    const key = `${this.sel.shirtColor}-${this.sel.trouserColor}`;
    this.container.querySelectorAll('.bundle-set__thumb').forEach((t) => {
      t.classList.toggle('is-active', `${t.dataset.shirtColor}-${t.dataset.trouserColor}` === key);
    });
  }

  _bindThumbArrows() {
    const track = this.container.querySelector('.bundle-set__thumbs');
    const prev = this.container.querySelector('.bundle-set__thumb-arrow--prev');
    const next = this.container.querySelector('.bundle-set__thumb-arrow--next');
    if (!track || (!prev && !next)) return;

    const step = () => Math.max(track.clientWidth * 0.8, 120);
    const update = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
    };

    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update));
    window.addEventListener('resize', update);
    update();
  }

  _setSwatchSelected(context, color) {
    this.container.querySelectorAll(`.bundle-set__swatch[data-context="${context}"]`).forEach((s) => {
      const active = s.dataset.color === color;
      s.classList.toggle('is-selected', active);
      s.setAttribute('aria-pressed', String(active));
    });
  }

  _updateSizes(item, color) {
    const map = this.data.variants[`${item}s`]?.[color];
    if (item === 'shirt') this.sel.shirtSize = null;
    else this.sel.trouserSize = null;
    this.container.querySelectorAll(`.bundle-set__size[data-item="${item}"]`).forEach((btn) => {
      // if product not configured in customizer, keep sizes as rendered (don't disable)
      if (!map) { btn.classList.remove('is-selected'); return; }
      const info = map[btn.dataset.size];
      btn.disabled = !info?.available;
      btn.classList.toggle('is-unavailable', !info?.available);
      btn.classList.remove('is-selected');
    });
    this._selectDefaultSize(item);
  }

  _selectDefaultSize(item) {
    const size = this.data.defaults.size;
    let btn = this.container.querySelector(`.bundle-set__size[data-item="${item}"][data-size="${size}"]`);
    if (!btn || btn.disabled) {
      // default size unavailable → pick the first available size
      btn = Array.from(this.container.querySelectorAll(`.bundle-set__size[data-item="${item}"]`)).find((b) => !b.disabled);
    }
    if (!btn) return;
    btn.classList.add('is-selected');
    const chosen = btn.dataset.size;
    if (item === 'shirt') this.sel.shirtSize = chosen;
    else this.sel.trouserSize = chosen;
  }

  _bindSizes() {
    this.container.querySelectorAll('.bundle-set__size').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const { item, size } = btn.dataset;
        this.container.querySelectorAll(`.bundle-set__size[data-item="${item}"]`).forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        if (item === 'shirt') this.sel.shirtSize = size;
        else this.sel.trouserSize = size;
      });
    });
  }

  _imageKey() {
    return `${this.sel.shirtColor}-${this.sel.trouserColor}`;
  }

  _updateImage() {
    if (!this.image) return;
    const key = this._imageKey();
    const url = this.data.images[key] || this.data.fallbackImage;
    if (key === this.currentImageKey) return;
    if (!url) return;
    this.currentImageKey = key;
    this.image.classList.add('is-loading');
    const tmp = new Image();
    tmp.onload = () => {
      this.image.srcset = '';  // clear srcset or browser ignores src change
      this.image.src = url;
      this.image.classList.remove('is-loading');
    };
    tmp.onerror = () => { this.image.classList.remove('is-loading'); };
    tmp.src = url;
  }

  _shirtColor()   { return this.sel.shirtColor; }
  _trouserColor() { return this.sel.trouserColor; }

  _validate() {
    if (!this.sel.shirtSize)   return 'Please select a shirt size.';
    if (!this.sel.trouserSize) return 'Please select a trouser size.';
    if (!this.data.bundleVariantId) return 'Bundle product not configured — assign it in the customizer.';
    const sv = this.data.variants.shirts[this._shirtColor()]?.[this.sel.shirtSize];
    const tv = this.data.variants.trousers[this._trouserColor()]?.[this.sel.trouserSize];
    if (!sv?.available) return 'Selected shirt size is out of stock.';
    if (!tv?.available) return 'Selected trouser size is out of stock.';
    return null;
  }

  async _addToCart({ checkout = false } = {}) {
    this.error.hidden = true;
    const err = this._validate();
    if (err) { this.error.textContent = err; this.error.hidden = false; return; }

    const sc = this._shirtColor(), tc = this._trouserColor();
    const shirtId   = this.data.variants.shirts[sc][this.sel.shirtSize].id;
    const trouserId = this.data.variants.trousers[tc][this.sel.trouserSize].id;
    // Link the two lines as one set; the "Bundle Discount" automatic discount
    // (Buy 1 shirt → get 1 trouser -148 EGP) applies per pair automatically.
    const bundleId = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());

    const activeBtn = checkout ? this.buyNow : this.cta;
    const originalText = activeBtn?.textContent;
    activeBtn?.setAttribute('aria-busy', 'true');
    if (activeBtn) activeBtn.textContent = checkout ? 'Checking out...' : 'Adding...';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            { id: shirtId,   quantity: 1, properties: { 'Set': 'The Linen Set', '_set_id': bundleId } },
            { id: trouserId, quantity: 1, properties: { 'Set': 'The Linen Set', '_set_id': bundleId } },
          ],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to cart.');
      if (checkout) { window.location.href = '/checkout'; return; }
      await this._refreshDrawer(data.id);
    } catch (e) {
      this.error.textContent = e.message; this.error.hidden = false;
    } finally {
      activeBtn?.removeAttribute('aria-busy');
      if (activeBtn && originalText) activeBtn.textContent = originalText;
    }
  }

  async _refreshDrawer(productId) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) { window.location.href = '/cart'; return; }
    await _refreshCartDrawer(drawer);
  }
}

// ── Cross-sell on product pages ──
class BundleCrosssell {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    const dataEl = document.getElementById(`CrosssellData-${this.sectionId}`);
    if (!dataEl) { console.error(`[BundleCrosssell] CrosssellData-${this.sectionId} not found`); return; }

    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[BundleCrosssell] Failed to parse data island JSON:', e);
      return;
    }

    this.sel = { currentSize: null, complementColor: this.data.productColor || 'Beige', complementSize: null };
    this.thumb = document.getElementById(`CrosssellThumb-${this.sectionId}`);
    this.sizesContainer = document.getElementById(`CrosssellSizes-${this.sectionId}`);
    this.cta   = document.getElementById(`CrosssellCta-${this.sectionId}`);
    this.error = document.getElementById(`CrosssellError-${this.sectionId}`);

    if (!this.cta) return;
    this._bindCurrentSizes();
    this._bindComplementSwatches();
    this._bindComplementSizes();
    this.cta.addEventListener('click', () => this._addToCart());
  }

  _bindCurrentSizes() {
    this.container.querySelectorAll(`.bundle-set__size[data-item="${this.data.productCategory}"]`).forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this.container.querySelectorAll(`.bundle-set__size[data-item="${this.data.productCategory}"]`).forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.sel.currentSize = btn.dataset.size;
      });
    });
  }

  _bindComplementSwatches() {
    this.container.querySelectorAll('.bundle-set__swatch[data-context="complement"]').forEach((sw) => {
      sw.addEventListener('click', () => {
        const { color } = sw.dataset;
        this.sel.complementColor = color;
        this.sel.complementSize = null;
        this.container.querySelectorAll('.bundle-set__swatch[data-context="complement"]').forEach((s) => {
          s.classList.toggle('is-selected', s.dataset.color === color);
          s.setAttribute('aria-pressed', String(s.dataset.color === color));
        });
        const prod = this.data.complementProducts[color];
        if (prod?.featuredImage && this.thumb) this.thumb.src = prod.featuredImage;
        this._renderComplementSizes(color);
      });
    });
  }

  _renderComplementSizes(color) {
    const variants = this.data.complementProducts[color]?.variants ?? {};
    this.sizesContainer?.querySelectorAll('.bundle-set__size[data-item="complement"]').forEach((btn) => {
      const info = variants[btn.dataset.size];
      btn.disabled = !info?.available;
      btn.classList.toggle('is-unavailable', !info?.available);
      btn.classList.remove('is-selected');
    });
    this.sel.complementSize = null;
  }

  _bindComplementSizes() {
    this.container.querySelectorAll('.bundle-set__size[data-item="complement"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this.container.querySelectorAll('.bundle-set__size[data-item="complement"]').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this.sel.complementSize = btn.dataset.size;
      });
    });
  }

  async _addToCart() {
    this.error.hidden = true;
    if (!this.sel.currentSize)    { this.error.textContent = `Please select a ${this.data.productCategory} size.`; this.error.hidden = false; return; }
    if (!this.sel.complementSize) { this.error.textContent = "Please select the other item's size."; this.error.hidden = false; return; }

    const isShirt = this.data.productCategory === 'shirt';
    const sc = isShirt ? this.data.productColor : this.sel.complementColor;
    const tc = isShirt ? this.sel.complementColor : this.data.productColor;
    const ss = isShirt ? this.sel.currentSize : this.sel.complementSize;
    const ts = isShirt ? this.sel.complementSize : this.sel.currentSize;
    const shirtId   = isShirt ? this.data.currentVariants[ss]?.id : this.data.complementProducts[sc]?.variants[ss]?.id;
    const trouserId = isShirt ? this.data.complementProducts[tc]?.variants[ts]?.id : this.data.currentVariants[ts]?.id;
    const bundleImage = this.data.bundleProductImage || this.data.complementProducts[this.sel.complementColor]?.featuredImage || '';
    const bundleVariantId = (this.data.bundleVariants && this.data.bundleVariants[`${sc}-${tc}`]) || this.data.bundleVariantId;

    this.cta.setAttribute('aria-busy', 'true');
    this.cta.textContent = 'Adding...';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: bundleVariantId, quantity: 1,
          properties: {
            'Shirt Size': ss,
            'Trouser Size': ts,
            '_shirt_variant_id': String(shirtId),
            '_trouser_variant_id': String(trouserId),
            '_bundle_id': crypto.randomUUID(),
            '_bundle_image': bundleImage,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to cart.');
      await this._refreshDrawer(data.id);
    } catch (e) {
      this.error.textContent = e.message; this.error.hidden = false;
    } finally {
      this.cta.removeAttribute('aria-busy'); this.cta.textContent = 'Add Set to Cart';
    }
  }

  async _refreshDrawer(productId) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) { window.location.href = '/cart'; return; }
    await _refreshCartDrawer(drawer);
  }
}

// ── Init ──
document.querySelectorAll('[id^="bundle-set-"]').forEach((el) => {
  try { new BundleSet(el); }
  catch (e) { console.error('[BundleSet] init error:', e); }
});
document.querySelectorAll('.bundle-crosssell[data-section-id]').forEach((el) => {
  try { new BundleCrosssell(el); }
  catch (e) { console.error('[BundleCrosssell] init error:', e); }
});
